import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  ChallengeNameType,
} from "@aws-sdk/client-cognito-identity-provider";

import { CognitoTokens } from "./authentication-cognito";
import { getCognitoConfig } from "./cognito-config";
import logger from "../utils/logger";

/**
 * Interface for Cognito magic link authentication state
 */
export interface CognitoMagicLinkState {
  email: string;
  session: string;
}

/**
 * Interface for magic link verification response
 */
export interface MagicLinkVerificationResult {
  success: boolean;
  tokens?: CognitoTokens;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Create Cognito Identity Provider client
 */
const getCognitoClient = () => {
  const config = getCognitoConfig();
  if (!config) return null;

  return new CognitoIdentityProviderClient({
    region: config.region,
  });
};

/**
 * Initiate magic link authentication with Cognito using CUSTOM_AUTH flow
 * This sends a magic link email via Lambda trigger
 *
 * @param email - User's email address
 * @returns Promise resolving to magic link state (containing session for callback verification)
 * @throws Error if initiation fails or Cognito not configured
 *
 * @example
 * ```typescript
 * try {
 *   const state = await initiateMagicLinkAuth('user@example.com');
 *   // Show "check your email" message
 *   // Store session for later verification
 * } catch (error) {
 *   console.error('Failed to initiate magic link auth:', error);
 * }
 * ```
 */
export async function initiateMagicLinkAuth(
  email: string,
): Promise<CognitoMagicLinkState> {
  const config = getCognitoConfig();
  const client = getCognitoClient();

  if (!config || !client) {
    throw {
      code: "ConfigurationError",
      message: "Cognito is not configured",
    };
  }

  try {
    const command = new InitiateAuthCommand({
      AuthFlow: "CUSTOM_AUTH",
      ClientId: config.clientId,
      AuthParameters: {
        USERNAME: email,
      },
    });

    const response = await client.send(command);

    // For CUSTOM_AUTH, we should receive a CUSTOM_CHALLENGE
    if (
      response.ChallengeName === ChallengeNameType.CUSTOM_CHALLENGE &&
      response.Session
    ) {
      logger.info("Magic link challenge received", {
        challengeName: response.ChallengeName,
      });

      return {
        email,
        session: response.Session,
      };
    } else {
      throw {
        code: "UnexpectedResponse",
        message: `Unexpected response: ${response.ChallengeName || "No challenge"}`,
      };
    }
  } catch (error: any) {
    logger.error("Magic link initiation failed", {
      error: error?.message || "Unknown error",
      code: error?.name || error?.code,
    });
    throw {
      code: error.name || error.code || "InitiationFailed",
      message: error.message || "Failed to initiate magic link auth",
    };
  }
}

/**
 * Verify magic link and complete authentication
 * This is called when user clicks the magic link in their email
 *
 * @param email - User's email address from magic link URL
 * @param signature - Cryptographic signature from magic link URL
 * @param issuedAt - Timestamp when link was issued
 * @param expiresAt - Timestamp when link expires
 * @param session - Optional session from initiateMagicLinkAuth (for same browser)
 * @returns Promise resolving to verification result with tokens if successful
 *
 * @example
 * ```typescript
 * // From magic link URL: /auth/magic-link?username=...&signature=...&issuedAt=...&expiresAt=...
 * try {
 *   const result = await verifyMagicLink(
 *     'user@example.com',
 *     'abc123...',
 *     1234567890,
 *     1234568790
 *   );
 *   if (result.success && result.tokens) {
 *     // Redirect with tokens
 *     redirectUser({ redirectUrl, tokens: result.tokens });
 *   }
 * } catch (error) {
 *   console.error('Magic link verification error:', error);
 * }
 * ```
 */
export async function verifyMagicLink(
  email: string,
  signature: string,
  issuedAt: number,
  expiresAt: number,
  session?: string,
): Promise<MagicLinkVerificationResult> {
  const config = getCognitoConfig();
  const client = getCognitoClient();

  if (!config || !client) {
    return {
      success: false,
      error: {
        code: "ConfigurationError",
        message: "Cognito is not configured",
      },
    };
  }

  // Check if link is expired before calling Cognito
  const now = Date.now();
  if (now > expiresAt) {
    return {
      success: false,
      error: {
        code: "LinkExpired",
        message: "This link has expired. Please request a new one.",
      },
    };
  }

  // If no session provided (different browser/device), initiate auth first
  // This enables cross-browser magic links
  if (!session) {
    console.log(
      "No session found - initiating auth for cross-browser verification",
    );
    try {
      const state = await initiateMagicLinkAuth(email);
      session = state.session;
      console.log(
        "New session created successfully for cross-browser magic link",
      );
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: error.code || "SessionInitFailed",
          message: error.message || "Failed to initialize verification session",
        },
      };
    }
  }

  // Prepare challenge answer with magic link parameters
  const challengeAnswer = JSON.stringify({
    username: email,
    signature,
    issuedAt,
    expiresAt,
  });

  try {
    const command = new RespondToAuthChallengeCommand({
      ChallengeName: ChallengeNameType.CUSTOM_CHALLENGE,
      ClientId: config.clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: email,
        ANSWER: challengeAnswer,
      },
    });

    const response = await client.send(command);

    // Check if authentication was successful
    if (response.AuthenticationResult) {
      const { IdToken, AccessToken, RefreshToken } =
        response.AuthenticationResult;

      if (IdToken && AccessToken && RefreshToken) {
        return {
          success: true,
          tokens: {
            idToken: IdToken,
            accessToken: AccessToken,
            refreshToken: RefreshToken,
          },
        };
      }
    }

    // If we got here, authentication failed
    return {
      success: false,
      error: {
        code: "VerificationFailed",
        message: "Magic link verification failed",
      },
    };
  } catch (error: any) {
    logger.error("Magic link verification failed", {
      error: error?.message || "Unknown error",
      code: error?.name || error?.code,
    });

    let errorMessage = "Magic link verification failed";
    if (error.name === "NotAuthorizedException") {
      errorMessage = "This link has already been used or is invalid.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: {
        code: error.name || error.code || "VerificationFailed",
        message: errorMessage,
      },
    };
  }
}
